import { useEffect, useState, type ReactNode } from 'react';
import { getRankingGuilds, getRankingPoints, getRankingPower, type GuildRank, type RankPlayer } from '@/shared/services/rankingService';
import { CLASSES_PT } from '@/features/Admin/labels';
import { formatScore } from '@/shared/lib/war';
import { SectionTitle } from './SectionTitle';

type Tab = 'power' | 'war' | 'guild';

const TABS: { key: Tab; label: string }[] = [
  { key: 'power', label: '♨ Poder' },
  { key: 'war', label: '⚔ Guerra' },
  { key: 'guild', label: '♛ Guildas' },
];

const HEADERS: Record<Tab, string[]> = {
  power: ['#', 'Nome', 'Classe', 'Guilda', 'PA (PVE)', 'PD (PVE)', 'Combate'],
  war: ['#', 'Nome', 'Classe', 'Guilda', 'Pontos', 'Kills'],
  guild: ['#', 'Guilda', 'Membros', 'Poder total', 'Pontos de guerra', 'Kills'],
};

// Ate 50 registros; o container mostra ~8 e o resto vai no scroll (.rank-scroll).
const MAX_ROWS = 50;

function RankNum({ pos }: { pos: number }) {
  const podium = pos === 1 ? 'one' : pos === 2 ? 'two' : pos === 3 ? 'three' : '';
  return <span className={`rank-num ${podium}`}>{pos}</span>;
}

export function RankingSection() {
  const [tab, setTab] = useState<Tab>('power');
  const [players, setPlayers] = useState<RankPlayer[]>([]);
  const [guilds, setGuilds] = useState<GuildRank[]>([]);

  useEffect(() => {
    if (tab === 'guild') {
      getRankingGuilds(50).then(setGuilds).catch(() => setGuilds([]));
    } else {
      const load = tab === 'war' ? getRankingPoints : getRankingPower;
      load(0, 0).then(setPlayers).catch(() => setPlayers([]));
    }
  }, [tab]);

  const limit = MAX_ROWS;
  const cls = (id: number) => CLASSES_PT[id] || '—';

  let rows: ReactNode;
  if (tab === 'guild') {
    rows = guilds.slice(0, limit).map((g, i) => (
      <tr key={g.guildName}>
        <td><RankNum pos={i + 1} /></td>
        <td><b>{g.guildName}</b>{i === 0 ? ' ♛' : ''}</td>
        <td>{g.memberCount}</td>
        <td>{formatScore(g.powerTotal ?? 0)}</td>
        <td>{formatScore(g.warPoints ?? 0)}</td>
        <td><b>{(g.warKills ?? 0).toLocaleString('pt-BR')}</b></td>
      </tr>
    ));
  } else {
    rows = players.slice(0, limit).map((p, i) => (
      <tr key={`${p.characterName}-${i}`}>
        <td><RankNum pos={i + 1} /></td>
        <td><b>{p.characterName}</b>{i === 0 ? ' ♛' : ''}</td>
        <td>{cls(p.classId)}</td>
        <td>{p.guildName || '—'}</td>
        {tab === 'power' ? (
          <>
            <td>{formatScore(p.pveAttack ?? 0)}</td>
            <td>{formatScore(p.pveDefense ?? 0)}</td>
            <td><b>{formatScore(p.combat ?? ((p.pveAttack ?? 0) + (p.pveDefense ?? 0)))}</b></td>
          </>
        ) : (
          <>
            <td>{formatScore(p.warPoint ?? 0)}</td>
            <td><b>{(p.kills ?? 0).toLocaleString('pt-BR')}</b></td>
          </>
        )}
      </tr>
    ));
  }
  const empty = tab === 'guild' ? guilds.length === 0 : players.length === 0;

  return (
    <section className="section ranking" id="ranking">
      <div className="shell">
        <SectionTitle
          title="Ranking"
          subtitle="Os maiores guerreiros de Nevareth. Mostre seu poder ao mundo."
        />
        <div className="rank-tabs reveal">
          {TABS.map((t) => (
            <button key={t.key} className={tab === t.key ? 'active' : undefined} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="rank-table-wrap fantasy-frame reveal">
          <div className="rank-scroll lb-scroll">
          <table>
            <thead><tr>{HEADERS[tab].map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {empty ? (
                <tr style={{ background: 'none' }}><td colSpan={HEADERS[tab].length}>Sem dados</td></tr>
              ) : rows}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </section>
  );
}
